import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Send } from 'lucide-react';
import { useEffect, useState } from 'react';

import { messageFromApiErrorBody } from '../../common/apiErrorMessage';
import { standaloneFieldSx } from '../../common/fieldStyles';
import { formatPhone } from '../../common/formatters';
import { httpClient } from '../../common/httpclient';

// Modal para conferir/editar o número e enviar o PDF da ordem de serviço por WhatsApp
// (`GET /orderservices/{id}/whatsapp/recipient` + `POST /orderservices/{id}/whatsapp/send`).
// `onResult(message, severity)` devolve o feedback para o snackbar da tela.
export default function OrderWhatsappDialog({ open, orderId, orderNumber, onClose, onResult }) {
    const [phone, setPhone] = useState('');
    const [loadingPhone, setLoadingPhone] = useState(false);
    const [sending, setSending] = useState(false);

    useEffect(() => {
        if (!open || !orderId) return undefined;
        let active = true;
        setPhone('');
        setLoadingPhone(true);
        httpClient.get(`/orderservices/${orderId}/whatsapp/recipient`)
            .then((response) => { if (active) setPhone(formatPhone(response.data?.phone || '')); })
            .catch((error) => console.error('Erro ao buscar celular do cliente:', error)) // usuário ainda pode digitar o número
            .finally(() => { if (active) setLoadingPhone(false); });
        return () => { active = false; };
    }, [open, orderId]);

    const handleSend = async () => {
        if (!orderId) return;
        try {
            setSending(true);
            const response = await httpClient.post(`/orderservices/${orderId}/whatsapp/send`, { phone });
            // validateStatus do httpClient aceita 4xx/5xx: o erro vem em response.data.
            if (response.status >= 400) {
                const data = response.data;
                onResult((typeof data === 'string' && data.trim()) || messageFromApiErrorBody(data) || 'Erro ao enviar a ordem de serviço por WhatsApp', 'error');
                return;
            }
            onClose();
            onResult('Ordem de serviço enviada por WhatsApp', 'success');
        } catch (error) {
            console.error('Erro ao enviar ordem de serviço por WhatsApp:', error);
            const data = error.response?.data;
            onResult((typeof data === 'string' && data.trim()) || messageFromApiErrorBody(data) || 'Erro ao enviar a ordem de serviço por WhatsApp', 'error');
        } finally {
            setSending(false);
        }
    };

    return (
        <Dialog open={open} onClose={() => !sending && onClose()} fullWidth maxWidth="xs">
            <DialogTitle>Enviar por WhatsApp</DialogTitle>
            <DialogContent>
                <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 2 }}>
                    O PDF da ordem de serviço nº {orderNumber} será enviado ao número abaixo. Confira antes de enviar.
                </Typography>
                <TextField
                    label="Telefone (WhatsApp)"
                    helperText={loadingPhone ? 'Buscando celular do cliente...' : 'Celular do cliente (editável). DDD + número, ex.: (11) 99999-9999'}
                    fullWidth
                    autoFocus
                    sx={standaloneFieldSx}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={sending}>Cancelar</Button>
                <Button
                    variant="contained"
                    onClick={handleSend}
                    disabled={sending || !phone.trim()}
                    startIcon={sending ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : <Send size={14} strokeWidth={2} />}
                >
                    Enviar
                </Button>
            </DialogActions>
        </Dialog>
    );
}
